// src/hooks/use-seo.ts
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  ogImage?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  structuredData?: object;
}

export const useSEO = (seo: SEOProps) => {
  useEffect(() => {
    // Update title
    if (seo.title) {
      document.title = seo.title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update description
    if (seo.description) {
      updateMetaTag('description', seo.description);
    }

    // Update keywords
    if (seo.keywords) {
      updateMetaTag('keywords', seo.keywords);
    }

    // Update Open Graph tags
    if (seo.ogTitle) {
      updateMetaTag('og:title', seo.ogTitle, true);
    }
    if (seo.ogDescription) {
      updateMetaTag('og:description', seo.ogDescription, true);
    }
    if (seo.ogType) {
      updateMetaTag('og:type', seo.ogType, true);
    }
    if (seo.ogUrl) {
      updateMetaTag('og:url', seo.ogUrl, true);
    }
    if (seo.ogImage) {
      updateMetaTag('og:image', seo.ogImage, true);
    }

    // Update Twitter Card tags
    if (seo.twitterCard) {
      updateMetaTag('twitter:card', seo.twitterCard);
    }
    if (seo.twitterTitle) {
      updateMetaTag('twitter:title', seo.twitterTitle);
    }
    if (seo.twitterDescription) {
      updateMetaTag('twitter:description', seo.twitterDescription);
    }
    if (seo.twitterImage) {
      updateMetaTag('twitter:image', seo.twitterImage);
    }

    // Update canonical URL
    if (seo.canonical) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = seo.canonical;
    }

    // Add structured data
    if (seo.structuredData) {
      // Remove existing structured data for this page
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(seo.structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      // Remove structured data script on unmount
      const script = document.querySelector('script[type="application/ld+json"]');
      if (script) {
        script.remove();
      }
    };
  }, [seo]);
};
